import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Shield, 
  FileText, 
  Building2, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Save,
  ExternalLink,
  Info
} from 'lucide-react';

const VeriFactuSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  const restaurantId = selectedRestaurant?.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    nif: '',
    razon_social: '',
    direccion_fiscal: '',
    invoice_series_prefix: 'A',
    print_qr: true,
    print_legend: true,
    representation_authorized: false,
    authorization_date: null,
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [authReference, setAuthReference] = useState('');

  useEffect(() => {
    if (restaurantId) {
      fetchSettings();
    }
  }, [restaurantId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/verifactu/settings/${restaurantId}`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching VeriFactu settings:', error);
      toast.error('Failed to load VeriFactu settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/verifactu/settings/${restaurantId}`, {
        nif: settings.nif,
        razon_social: settings.razon_social,
        direccion_fiscal: settings.direccion_fiscal,
        invoice_series_prefix: settings.invoice_series_prefix,
        print_qr: settings.print_qr,
        print_legend: settings.print_legend,
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    if (!acceptTerms) {
      toast.error('You must accept the VeriFactu terms');
      return;
    }

    if (!settings.nif || !settings.razon_social) {
      toast.error('Please fill in NIF and Razon Social first');
      return;
    }

    try {
      setSaving(true);
      await api.post(`/verifactu/settings/${restaurantId}/enable`, {
        accept_terms: true,
        authorization_reference: authReference || null,
      });
      setSettings(prev => ({ ...prev, enabled: true, representation_authorized: true }));
      toast.success('VeriFactu enabled successfully');
    } catch (error) {
      console.error('Error enabling VeriFactu:', error);
      toast.error(error.response?.data?.detail || 'Failed to enable VeriFactu');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable VeriFactu? This will stop generating fiscal invoices.')) {
      return;
    }

    try {
      setSaving(true);
      await api.post(`/verifactu/settings/${restaurantId}/disable`);
      setSettings(prev => ({ ...prev, enabled: false }));
      toast.success('VeriFactu disabled');
    } catch (error) {
      console.error('Error disabling VeriFactu:', error);
      toast.error(error.response?.data?.detail || 'Failed to disable VeriFactu');
    } finally {
      setSaving(false);
    }
  };

  if (!restaurantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a restaurant first</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-orange-500" />
            VeriFactu Settings
          </h1>
          <p className="text-gray-500 mt-1">Spain Anti-Fraud Invoicing System Configuration</p>
        </div>
        <Button onClick={() => navigate(`/restaurant/verifactu`)} variant="outline">
          <FileText className="h-4 w-4 mr-2" />
          View Invoices
        </Button>
      </div>

      {/* Status Banner */}
      {settings.enabled ? (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">VeriFactu Active</AlertTitle>
          <AlertDescription className="text-green-700">
            Fiscal invoices are being generated automatically for all paid orders.
            {settings.authorization_date && (
              <span className="block mt-1">
                Authorized on: {new Date(settings.authorization_date).toLocaleDateString()}
              </span>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">VeriFactu Not Active</AlertTitle>
          <AlertDescription className="text-amber-700">
            Configure your fiscal data below and enable VeriFactu to comply with Spain's anti-fraud regulations.
          </AlertDescription>
        </Alert>
      )}

      {/* Fiscal Data Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-gray-600" />
            Fiscal Data (Datos Fiscales)
          </CardTitle>
          <CardDescription>
            Enter your Spanish tax information exactly as registered with AEAT
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nif">NIF (Tax ID) *</Label>
              <Input
                id="nif"
                data-testid="verifactu-nif-input"
                placeholder="B12345678"
                value={settings.nif || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, nif: e.target.value.toUpperCase() }))}
                disabled={settings.enabled}
              />
              <p className="text-xs text-gray-500">Spanish tax identification number</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="series">Invoice Series</Label>
              <Input
                id="series"
                data-testid="verifactu-series-input"
                placeholder="A"
                value={settings.invoice_series_prefix || 'A'}
                onChange={(e) => setSettings(prev => ({ ...prev, invoice_series_prefix: e.target.value.toUpperCase() }))}
                maxLength={2}
                disabled={settings.enabled}
              />
              <p className="text-xs text-gray-500">Prefix for invoice numbers (e.g., A-00001)</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="razon">Razon Social (Legal Name) *</Label>
            <Input
              id="razon"
              data-testid="verifactu-razon-input"
              placeholder="Mi Restaurante S.L."
              value={settings.razon_social || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, razon_social: e.target.value }))}
              disabled={settings.enabled}
            />
            <p className="text-xs text-gray-500">Legal business name as registered</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="direccion">Fiscal Address (Direccion Fiscal) *</Label>
            <Input
              id="direccion"
              data-testid="verifactu-direccion-input"
              placeholder="Calle Principal 123, 28001 Madrid"
              value={settings.direccion_fiscal || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, direccion_fiscal: e.target.value }))}
              disabled={settings.enabled}
            />
            <p className="text-xs text-gray-500">Registered business address</p>
          </div>

          {!settings.enabled && (
            <Button onClick={handleSave} disabled={saving} className="mt-4">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Receipt Options Card */}
      <Card>
        <CardHeader>
          <CardTitle>Receipt Options</CardTitle>
          <CardDescription>Configure how VeriFactu data appears on receipts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Print QR Code</Label>
              <p className="text-sm text-gray-500">Show AEAT verification QR on receipts</p>
            </div>
            <Switch
              data-testid="verifactu-qr-switch"
              checked={settings.print_qr}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, print_qr: checked }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Print VeriFactu Legend</Label>
              <p className="text-sm text-gray-500">Show "VERI*FACTU - Factura verificable" text</p>
            </div>
            <Switch
              data-testid="verifactu-legend-switch"
              checked={settings.print_legend}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, print_legend: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Enable/Disable Card */}
      <Card className={settings.enabled ? 'border-green-200' : 'border-amber-200'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {settings.enabled ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            {settings.enabled ? 'VeriFactu is Enabled' : 'Enable VeriFactu'}
          </CardTitle>
          <CardDescription>
            {settings.enabled 
              ? 'VeriFactu is active. Fiscal invoices are generated for all paid orders.'
              : 'Authorize Hambre to send fiscal invoices to AEAT on your behalf.'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {settings.enabled ? (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <Badge className="ml-2 bg-green-100 text-green-800">Active</Badge>
                  </div>
                  <div>
                    <span className="text-gray-500">Next Invoice:</span>
                    <span className="ml-2 font-mono">{settings.invoice_series_prefix}-{String(settings.next_invoice_number || 1).padStart(5, '0')}</span>
                  </div>
                  {settings.authorization_reference && (
                    <>
                      <div>
                        <span className="text-gray-500">Authorization:</span>
                        <span className="ml-2 font-mono text-xs">{settings.authorization_reference}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Authorized:</span>
                        <span className="ml-2">{settings.authorization_date ? new Date(settings.authorization_date).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Authorization Document Download */}
              <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">Authorization Document</p>
                    <p className="text-sm text-green-600">Legal proof of AEAT collaboration authorization</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await api.get(`/verifactu/settings/${restaurantId}/authorization/download`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `authorization_${settings.authorization_reference || 'document'}.txt`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (error) {
                        toast.error('Failed to download authorization document');
                      }
                    }}
                    data-testid="download-authorization-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>
              
              <Button 
                variant="destructive" 
                onClick={handleDisable}
                disabled={saving}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Disable VeriFactu
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700">
                  By enabling VeriFactu, you authorize AZTRIX MEDIA LTD (Hambre) as your fiscal collaborator 
                  (colaborador social Tipo 017) to submit invoice records to the Spanish Tax Agency (AEAT) 
                  on your behalf, in accordance with Royal Decree 1007/2023.
                </AlertDescription>
              </Alert>

              {/* Authorization Declaration Box */}
              <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
                <p className="text-sm font-medium mb-2">AUTORIZACIÓN / AUTHORIZATION</p>
                <p className="text-xs text-gray-600 mb-3">
                  Yo, en calidad de representante legal de <strong>{settings.razon_social || '[Su Empresa]'}</strong> 
                  (NIF: <strong>{settings.nif || '[NIF]'}</strong>), autorizo a AZTRIX MEDIA LTD a remitir los 
                  registros de facturación generados por mi sistema de facturación a la Agencia Estatal de 
                  Administración Tributaria mediante el sistema VERI*FACTU.
                </p>
                <p className="text-xs text-gray-500 italic">
                  I, as the legal representative of the above entity, authorize AZTRIX MEDIA LTD to submit 
                  invoice records generated by my invoicing system to the Spanish Tax Agency through the 
                  VERI*FACTU system.
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  data-testid="verifactu-terms-checkbox"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="terms"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I confirm and authorize (Confirmo y autorizo)
                  </label>
                  <p className="text-sm text-gray-500">
                    I confirm that the fiscal data provided is correct and complete. I authorize AZTRIX MEDIA LTD 
                    to act as my fiscal collaborator for VERI*FACTU submissions to AEAT. A legally binding 
                    authorization document will be generated and stored.
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleEnable} 
                disabled={saving || !acceptTerms || !settings.nif || !settings.razon_social}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Shield className="h-4 w-4 mr-2" />
                {saving ? 'Enabling...' : 'Enable VeriFactu'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Links */}
      <div className="flex gap-4 text-sm">
        <a 
          href="/verifactu" 
          target="_blank" 
          className="text-orange-600 hover:text-orange-700 flex items-center gap-1"
        >
          <ExternalLink className="h-4 w-4" />
          Learn more about VeriFactu
        </a>
        <a 
          href="https://sede.agenciatributaria.gob.es" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-gray-600 hover:text-gray-700 flex items-center gap-1"
        >
          <ExternalLink className="h-4 w-4" />
          AEAT Official Website
        </a>
      </div>
    </div>
  );
};

export default VeriFactuSettings;
